/**
 * @file        src/modals/CorporateRegisterModal.jsx
 * @description Corporate account self-registration → files an approval
 *              REQUEST (corporate_registration_requests). Nothing is created
 *              or signed in from here any more: an admin reviews the request
 *              in the admin console and the applicant is emailed the decision;
 *              on approval the account + owner login are created with the
 *              password chosen below. PAN is mandatory (invoicing), GSTIN is
 *              optional.
 * @author      Kinetika Labs
 * @created     2026-07-10
 * @changelog
 *   - 2026-09-12 Claude — Reopening the modal after a filed request showed the
 *                          "Request submitted" confirmation again instead of a
 *                          blank form: ModalsHost keeps every modal MOUNTED and
 *                          only toggles `open`, so this component's state
 *                          outlived a close. The confirmation (and the filed
 *                          request's field values) are now cleared on close.
 *   - 2026-08-26 Claude — GSTIN validation now checks the real GSTIN shape
 *                          (2-digit state + 5-letter/4-digit/1-letter PAN +
 *                          entity code + fixed Z + checksum) instead of just
 *                          "15 alphanumeric characters".
 *   - 2026-08-26 Claude — Contact phone now strips non-digits and caps at 10
 *                          characters as the user types, with a submit-time
 *                          check that a partial number isn't filed.
 *   - 2026-08-17 Claude — MOBILE: viewport-height cap switched from `vh` to `svh`.
 *                          On a phone browser `100vh` is the height with the toolbars
 *                          HIDDEN, so a vh-capped sheet is taller than what is actually
 *                          on screen and its last row — the action buttons — sat under
 *                          the toolbar. `svh` is the always-visible height, so the
 *                          bottom of the sheet is reachable on every device. Desktop is
 *                          unaffected (svh == vh there).
 *   - 2026-08-10 Claude — New approval flow: submit now files a registration
 *                          request (no account, no token, no auto-login) and
 *                          shows a "we'll email you" confirmation instead.
 *                          PAN is now required (GSTIN stays optional).
 *   - 2026-07-29 Claude — Brand sweep: the teal→green CTA gradient / solid teal
 *                          button + active-tab fills → brand black (#1C1C1C) with
 *                          white text. Teal is kept for non-button accents (dots,
 *                          ticks, badges, step circles, calendar cells).
 *   - 2026-08-04 Claude — Fits without scrolling now: icon + heading share one row,
 *                          the nine stacked fields are paired into five two-column
 *                          rows (company name stays full width), gap-4 → gap-3, and
 *                          the "You'll be logged in right away" footer + its shield
 *                          are gone. No field was added, removed or renamed — the
 *                          same keys still post to /corporate-register.
 *   - 2026-08-04 Claude — Password is now a required field. Without it
 *                          /corporate-register stores a RANDOM hash, so the owner it
 *                          creates can never use the Corporate log-in tab.
 *   - 2026-07-10 Claude — Initial (B2B agent onboarding), as AgentRegisterModal.jsx.
 *   - 2026-07-10 Claude — Hide the form's scrollbar.
 *   - 2026-07-27 Claude — Renamed to CorporateRegisterModal.jsx; posts to
 *                          /corporate-register (was /agent-register); the
 *                          "agent/reseller" concept is retired in favour of
 *                          corporate accounts.
 *   - 2026-09-18 Claude — The password field is now <PasswordField>, so the
 *                          value a company will log in with from here on can
 *                          be checked before the request is filed. There is no
 *                          confirm field, so a typo was unrecoverable without a
 *                          reset.
 */
import { useEffect, useState } from 'react';
import { post } from '../api/client';
import PasswordField from '../components/PasswordField';

const EMPTY = {
  agency_name: '', first_name: '', last_name: '', email: '', password: '',
  contact_phone: '', gst_number: '', pan_number: '', address: '',
};

// The backend enforces the same floor (/corporate-register rejects a shorter one).
const MIN_PASSWORD = 8;

const Field = ({ label, children, required }) => (
  <div>
    <label className="block text-[11px] font-semibold uppercase tracking-wide text-muted mb-1.5">
      {label} {required ? <span className="text-neon">*</span> : null}
    </label>
    {children}
  </div>
);

const inputCls =
  'w-full bg-night/70 rounded-xl border border-line px-4 py-2.5 outline-none text-[15px] ' +
  'placeholder:text-muted/50 focus:border-neon transition';

export default function CorporateRegisterModal({ open, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Holds the submitted email once the request is filed — flips the modal to
  // its "we'll email you" confirmation instead of signing anyone in.
  const [submitted, setSubmitted] = useState('');

  // ModalsHost renders every modal permanently and toggles `open`, so this
  // component is never unmounted and its state outlives a close. Without this,
  // a user who filed a request and reopened "Register here" was dropped back on
  // the confirmation screen instead of a fresh form. Reset happens on CLOSE (the
  // modal is already invisible, so the form never flashes the old confirmation),
  // and the fields are wiped only once a request was actually filed — an
  // accidentally-closed half-filled form is still there when it reopens.
  useEffect(() => {
    if (open) return;
    setBusy(false);
    setError('');
    if (submitted) { setForm(EMPTY); setSubmitted(''); }
  }, [open, submitted]);

  if (!open) return null;

  const set = (k) => (e) => { setForm({ ...form, [k]: e.target.value }); setError(''); };

  // Contact phone is optional but, if given, must be exactly 10 digits — no
  // letters, symbols or spaces, and typing past the 10th digit is dropped
  // rather than silently accepted and rejected later.
  const setPhone = (e) => {
    setForm({ ...form, contact_phone: e.target.value.replace(/\D/g, '').slice(0, 10) });
    setError('');
  };

  const submit = async () => {
    if (busy) return;
    if (!form.agency_name.trim()) { setError('Company name is required.'); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) { setError('Enter a valid email address.'); return; }
    // Required, because the corporate tab signs in with this password. It used to
    // be omitted entirely, and /corporate-register then stored a RANDOM hash — so
    // the owner it just created could never pass a password screen.
    if (form.password.length < MIN_PASSWORD) { setError(`Password must be at least ${MIN_PASSWORD} characters.`); return; }
    const pan = form.pan_number.trim().toUpperCase();
    const gst = form.gst_number.trim().toUpperCase();
    // PAN is mandatory (the backend refuses without it); GSTIN is optional but
    // format-checked when given so a typo doesn't bounce server-side.
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan)) { setError('A valid PAN is required (e.g. ABCDE1234F).'); return; }
    // Standard GSTIN shape: 2-digit state code, 5-letter + 4-digit + 1-letter
    // PAN, 1 alphanumeric entity code, the fixed 'Z', 1 alphanumeric checksum.
    if (gst && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}Z[0-9A-Z]{1}$/.test(gst)) {
      setError('Enter a valid 15-character GSTIN (e.g. 27ABCDE1234F1Z5).'); return;
    }
    if (form.contact_phone && form.contact_phone.length !== 10) { setError('Contact phone must be exactly 10 digits.'); return; }
    setBusy(true); setError('');
    try {
      const body = {
        ...form,
        agency_name: form.agency_name.trim(),
        email: form.email.trim(),
        gst_number: gst,
        pan_number: pan,
      };
      // Files an approval request — no account exists yet, so there is no
      // token to store and nobody to sign in. The admin's decision arrives by
      // email.
      await post('/corporate-register', body);
      setSubmitted(form.email.trim());
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not submit your registration request. Please try again.');
    } finally {
      setBusy(false);
    }
  };


  // Confirmation view — the request is filed; the decision arrives by email.
  if (submitted) {
    return (
      <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
        <div onClick={onClose} className="absolute inset-0 bg-night/85 backdrop-blur-sm" />
        <div className="relative w-full max-w-md rounded-3xl border border-line bg-panel p-8 text-center">
          <span className="mx-auto w-14 h-14 rounded-full bg-neon flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-7 h-7 text-onaccent"><path fill="currentColor" d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" /></svg>
          </span>
          <h2 className="font-display text-2xl font-extrabold mt-4">Request submitted</h2>
          <p className="text-muted text-sm mt-3">
            Thanks &mdash; your corporate account request is with our team for review. We&rsquo;ll
            email the decision to <span className="font-semibold">{submitted}</span> within
            24&ndash;48 hours. You can sign in once it&rsquo;s approved.
          </p>
          <button
            onClick={onClose}
            className="w-full mt-6 py-3 rounded-xl bg-[#1C1C1C] text-white font-bold hover:brightness-125 cursor-pointer transition"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
      <div onClick={onClose} className="absolute inset-0 bg-night/85 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg max-h-[90svh] overflow-y-auto no-scrollbar rounded-3xl border border-line bg-panel p-6">
        <button
          onClick={onClose}
          className="absolute top-5 right-5 w-9 h-9 rounded-lg bg-night border border-line flex items-center justify-center hover:border-neon cursor-pointer"
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5 text-muted"><path fill="currentColor" d="m12 10.6 4.9-4.9 1.4 1.4-4.9 4.9 4.9 4.9-1.4 1.4-4.9-4.9-4.9 4.9-1.4-1.4 4.9-4.9-4.9-4.9 1.4-1.4z" /></svg>
        </button>

        {/* Icon and heading on ONE row (matching AuthModal). Stacked, they cost
            ~64px of height before the first field — the single biggest reason this
            form used to scroll. */}
        <div className="flex items-center gap-3">
          <span className="w-12 h-12 shrink-0 rounded-full bg-neon flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-6 h-6 text-onaccent"><path fill="currentColor" d="M10 4h4a2 2 0 0 1 2 2v2h4a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2h4V6a2 2 0 0 1 2-2m0 4h4V6h-4z" /></svg>
          </span>
          <h2 className="font-display text-2xl font-extrabold leading-tight min-w-0">Register your company</h2>
        </div>
        <p className="text-muted text-sm mt-2">
          For corporate travel accounts. Submit your company details for review — we&rsquo;ll
          email you once your account is approved, then you can book for your team and
          manage employee logins in one place.
        </p>

        {error ? (
          <div className="mt-4 px-4 py-2.5 rounded-xl border border-warn/40 bg-warn/10 text-sm text-ink">
            {error}
          </div>
        ) : null}

        {/* FIVE rows, not nine. Every optional field is paired into a two-column
            row so the form fits a laptop window without scrolling; only the company
            name stays full width because it is the one that runs long. gap-3 rather
            than gap-4 for the same reason. Field order and names are unchanged — the
            same keys still go to /corporate-register. */}
        <div className="mt-5 grid gap-3">
          <Field label="Company name" required>
            <input className={inputCls} value={form.agency_name} onChange={set('agency_name')} placeholder="Skyline Travels" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="First name">
              <input className={inputCls} value={form.first_name} onChange={set('first_name')} placeholder="Asha" />
            </Field>
            <Field label="Last name">
              <input className={inputCls} value={form.last_name} onChange={set('last_name')} placeholder="Rao" />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Email address" required>
              <input type="email" className={inputCls} value={form.email} onChange={set('email')} placeholder="you@company.com" />
            </Field>
            {/* This is the credential the Corporate tab asks for. Without it the
                backend stores a random hash and the account can only ever get in by
                OTP. */}
            <Field label="Password" required>
              {/* lock={false}: every sibling in this form is a bare input, so the
                  glyph AuthModal's rows carry would single this one field out.
                  The default skin is already `inputCls`'s box, and the focus ring
                  comes from `.field:focus-within` rather than focus:border-neon. */}
              <PasswordField
                lock={false}
                className="transition"
                value={form.password}
                onChange={set('password')}
                autoComplete="new-password"
                minLength={MIN_PASSWORD}
                placeholder={`At least ${MIN_PASSWORD} characters`}
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Contact phone">
              <input
                type="tel"
                inputMode="numeric"
                maxLength={10}
                className={inputCls}
                value={form.contact_phone}
                onChange={setPhone}
                placeholder="9876543210"
              />
            </Field>
            <Field label="GSTIN">
              <input className={inputCls} maxLength={15} value={form.gst_number} onChange={set('gst_number')} placeholder="27ABCDE1234F1Z5" />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="PAN" required>
              <input className={inputCls} maxLength={10} value={form.pan_number} onChange={set('pan_number')} placeholder="ABCDE1234F" />
            </Field>
            <Field label="Registered address">
              <input className={inputCls} value={form.address} onChange={set('address')} placeholder="Office address" />
            </Field>
          </div>
        </div>

        <button
          onClick={submit}
          disabled={busy}
          className="w-full mt-5 py-3 rounded-xl bg-[#1C1C1C] text-white font-bold hover:brightness-125 cursor-pointer transition disabled:opacity-60"
        >
          {busy ? 'Submitting request…' : 'Submit registration request'}
        </button>
      </div>
    </div>
  );
}
